import React, { useEffect, useState } from "react";
import { FileCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function MonthlyInvoiceDialog({ customerId, customer, onCreated }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(thisMonth());
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    api.get("/ledger/monthly-invoice/preview", { params: { customer_id: customerId, month } })
      .then(({ data }) => setPreview(data)).catch(() => setPreview(null));
  }, [open, month, customerId]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/ledger/monthly-invoice", { customer_id: customerId, month });
      toast.success("Aylık fatura cariye işlendi");
      setOpen(false); onCreated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const disabled = saving || !preview || preview.total <= 0 || preview.already_invoiced;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="monthly-invoice-btn" className="h-11 rounded-full gap-2 font-semibold">
        <FileCheck className="h-4 w-4" /> Aylık Fatura Oluştur
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FileCheck className="h-5 w-5 text-primary" /> Aylık Fatura — {customer?.short_name || customer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-invoice-month" className="font-mono" />
            {preview ? (
              <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm" data-testid="monthly-invoice-preview">
                <Row l="Toplam Kişi" v={`${preview.headcount} kişi`} />
                {preview.meals?.length > 1 && preview.meals.map((m) => (
                  <div key={m.meal} className="flex justify-between text-xs text-muted-foreground pl-3" data-testid={`monthly-invoice-meal-${m.meal}`}>
                    <span>{m.meal} · {m.headcount} kişi × {TRY(m.unit_price_gross)}{preview.vat > 0 && " (KDV dahil)"}</span><span className="font-mono">{TRY(m.gross)}</span>
                  </div>
                ))}
                <Row l="Net Ciro" v={TRY(preview.net)} />
                {preview.vat > 0 && <Row l="KDV" v={TRY(preview.vat)} />}
                <div className="border-t border-border pt-2 flex justify-between font-semibold"><span>Fatura Tutarı{preview.vat > 0 && <span className="text-xs text-muted-foreground font-normal"> (KDV dahil)</span>}</span><span className="font-mono text-primary" data-testid="monthly-invoice-total">{TRY(preview.total)}</span></div>
                {preview.already_invoiced && <div className="text-xs text-amber-400 font-semibold" data-testid="monthly-invoice-dup">Bu ay için fatura zaten oluşturulmuş.</div>}
                {preview.total <= 0 && !preview.already_invoiced && <div className="text-xs text-muted-foreground">Bu ayda kişi sayısı kaydı yok.</div>}
              </div>
            ) : <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Hesaplanıyor…</div>}
            <p className="text-xs text-muted-foreground">Fatura tarihi bugün, vade müşterinin ödeme vadesi kadar sonra olacaktır.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={create} disabled={disabled} data-testid="monthly-invoice-save" className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Cariye İşle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Row = ({ l, v }) => <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-mono">{v}</span></div>;
